import { useState } from 'react';
import { Stack, Typography, Button, Alert } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { createFacility, type FacilityType } from '../../lib/facilities-api';
import { extractApiError } from '../../lib/api-client';
import { FacilityForm, type FacilityFormValues } from './form';
import { useT } from '../../i18n';

export function FacilityCreatePage() {
  const t = useT();
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(values: FacilityFormValues) {
    setSubmitting(true);
    setError(null);
    try {
      await createFacility({
        name: values.name.trim(),
        type: values.type as FacilityType,
        requires_booking: values.requires_booking,
        slot_minutes: Number(values.slot_minutes),
        requires_approval: values.requires_approval,
        ...(values.description.trim() ? { description: values.description.trim() } : {}),
        ...(values.capacity.trim() ? { capacity: Number(values.capacity) } : {}),
        ...(values.open_time ? { open_time: values.open_time } : {}),
        ...(values.close_time ? { close_time: values.close_time } : {}),
        ...(values.image_url.trim() ? { image_url: values.image_url.trim() } : {}),
      });
      navigate('/facilities');
    } catch (e) {
      setError(extractApiError(e).message);
      throw e;
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Stack spacing={3}>
      <Button
        startIcon={<ArrowBackIcon />}
        component={RouterLink}
        to="/facilities"
        sx={{ alignSelf: 'flex-start' }}
      >
        {t('fac.backToList')}
      </Button>

      <div>
        <Typography variant="h4" fontWeight={600}>
          {t('fac.createTitle')}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t('fac.createSubtitle')}
        </Typography>
      </div>

      {error ? <Alert severity="error">{error}</Alert> : null}

      <FacilityForm submitLabel={t('fac.submitCreate')} submitting={submitting} onSubmit={handleSubmit} />
    </Stack>
  );
}
